import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { normalizeRecommendedDeck, validateRecommendedDeck } from '../domain/recommendedDecks.js';

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('서비스 연결 설정을 확인할 수 없습니다.');
  return supabase;
}

function isMissingRecommendedDeckSchema(error) {
  const raw = String(error?.message || '');
  return /saved_squads|relation .* does not exist|could not find the table|column .*element|schema cache/i.test(raw);
}

function createRecommendedDeckError(error, fallback) {
  if (isMissingRecommendedDeckSchema(error)) {
    return new Error('추천덱 DB 설정이 적용되지 않았습니다. Supabase 추천덱 마이그레이션을 먼저 실행해주세요.');
  }
  return new Error(String(error?.message || fallback || '추천덱을 처리하지 못했습니다.'));
}

export async function loadRecommendedDecks({ unionId = '', unionName = '', viewerRole = '' } = {}) {
  const client = requireClient();
  const normalizedUnionId = String(unionId || '').trim();
  if (!normalizedUnionId) throw new Error('활성 유니온 가입 정보를 확인할 수 없습니다.');

  // The shell already owns the verified active-union identity through EntryGate.
  // Do not rebuild the whole raid overview just to discover the same union again.
  const result = await client
    .from('saved_squads')
    .select('id,union_id,title,element,deck_codes,note,created_at,updated_at')
    .eq('union_id', normalizedUnionId)
    .order('updated_at', { ascending: false })
    .limit(500);
  if (result.error) throw createRecommendedDeckError(result.error, '추천덱을 불러오지 못했습니다.');

  return {
    unionId: normalizedUnionId,
    unionName: String(unionName || ''),
    viewerRole: String(viewerRole || ''),
    decks: (Array.isArray(result.data) ? result.data : []).map((row) => normalizeRecommendedDeck(row)),
  };
}

export async function saveRecommendedDeck({ unionId, id = '', input }) {
  const client = requireClient();
  const validated = validateRecommendedDeck(input);
  if (!validated.ok) throw new Error(validated.message);
  const payload = {
    union_id: String(unionId || ''),
    title: validated.value.title,
    element: validated.value.element,
    deck_codes: validated.value.deck,
    note: validated.value.note || null,
    updated_at: new Date().toISOString(),
  };

  const result = id
    ? await client.from('saved_squads').update(payload).eq('id', String(id)).eq('union_id', String(unionId)).select('*').limit(1)
    : await client.from('saved_squads').insert(payload).select('*').limit(1);
  if (result.error) throw createRecommendedDeckError(result.error, '추천덱을 저장하지 못했습니다.');
  if (!result.data?.[0]) throw new Error('저장된 추천덱을 확인할 수 없습니다.');
  return normalizeRecommendedDeck(result.data[0]);
}

export async function deleteRecommendedDeck({ unionId, id }) {
  const client = requireClient();
  const result = await client
    .from('saved_squads')
    .delete()
    .eq('id', String(id || ''))
    .eq('union_id', String(unionId || ''))
    .select('id');
  if (result.error) throw createRecommendedDeckError(result.error, '추천덱을 삭제하지 못했습니다.');
  if (!result.data?.length) throw new Error('삭제할 추천덱을 찾을 수 없습니다.');
  return true;
}
